import { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

import { StakeCheckingWizard } from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";

import { useDeals, useStakeDebit, useUnclaimedTransactions } from "../../CustomHooks/Queries";
import { useCreateMyUtilitiesOrder, useRequestStakeDebitCard } from "../../CustomHooks/Mutations";

import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import { APIhandler } from "../../Utils/api";
import { APIQueryKeys } from "../../Utils/constants";
import {
  dispatchAPIErrorToast,
  getBattlecardCashBackAmountNumeric,
  getUserEmail,
  getUserId,
  getUserSfid,
  isStakeUtilitiesUserFromDealsResponse,
  isSVDEligible,
  onSignOut,
  shouldShowStakeUtilitiesPage,
  shouldShowStakeUtilitiesResumePage,
} from "../../Utils/helpers";

import { formatReward, screenForApplicationOutcome } from "../InstantRelease/instant-data";

import "../Onboarding/battle-cards.scss";
import "./stake-utilities.scss";

import Card from "./card";
import CardPair from "./card-pair";
import Celebration from "./celebration";
import CheckBadge from "./check-badge";
import Decide from "./decide";
import Goals, { GOALS } from "./goals";
import HeadlineBlock from "./headline-block";
import HomeDone from "./home-done";
import HomeServices from "./home-services";
import Landing from "./landing";
import PowerDone from "./power-done";
import PowerLoading from "./power-loading";
import PowerReview from "./power-review";
import PowerSignUp from "./power-sign-up";
import PrimaryButton from "./primary-button";
import SettingUpOverlay from "./setting-up-overlay";
import Shell from "./shell";
import Tiles, { TILE_ICONS } from "./tiles";
import Timeline from "./timeline";
import { POWER_LOADING_MS, POWER_SUBMIT_MS, UTILITIES_OFFER, UTILITIES_STEPS_ENABLED, formatMoney } from "./utilities-config";

/*
 * Stake Utilities onboarding, phase 1: opening the Stake account (Paper "Checking First").
 * It only runs when the backend marks the member as stake_utilities. The Stake Power and
 * TV and Internet steps are not part of this phase.
 */

const STATUS_POLL_INTERVAL_MS = 3 * 1000;
const APPLY_SETTLE_POLL_COUNT = 6;
const PENDING_TIMEOUT_MS = 10 * 60 * 1000;
const OPENED_ADVANCE_MS = 1600;

const WAITING_SCREENS = ["pending", "review", "details"];


// The apply flow reports "approved" once the deposit account exists. Here that is the "opened" screen.
const outcomeScreen = (stakeDebit) => {
  const screen = screenForApplicationOutcome(stakeDebit);
  return screen === "approved" ? "opened" : screen;
};

function StakeUtilities({ entryScreen, amount, email, onFinish, onSignOutClick }) {
  const queryClient = useQueryClient();
  const { show: showIntercom, update: updateIntercom } = useIntercom();

  // The app hides the Intercom launcher (the sidebar opens it), and this flow has no sidebar. Show it on every screen.
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: false });
    return () => updateIntercom({ hideDefaultLauncher: true });
  }, [updateIntercom]);

  const [screen, setScreen] = useState(entryScreen);
  const [goals, setGoals] = useState([]);
  const [isSettingUp, setIsSettingUp] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const amountLabel = formatReward(amount);

  const toggleGoal = (key) =>
    setGoals((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

  // Best effort: a failed save should never block opening the account.
  const continueFromGoals = (skip = false) => {
    if (!skip && goals.length) {
      const financialGoals = GOALS.filter((goal) => goals.includes(goal.key))
        .map((goal) => goal.label)
        .join(";");
      Promise.resolve(APIhandler.onboarding.saveSpendingFinancialGoals(getUserId(), { financial_goals: financialGoals })).catch(() => {});
    }
    setScreen("wizard");
  };

  const showOpened = useCallback(() => {
    setIsSettingUp(false);
    setScreen("opened");
  }, []);

  const showDenied = useCallback(() => {
    setIsSettingUp(false);
    setScreen("denied");
  }, []);

  // Right after applying the status is often still pending for a few seconds, so keep the
  // overlay up and check a few times before showing the pending screen.
  const showApplicationOutcome = useCallback(
    async (stakeDebit) => {
      let nextScreen = outcomeScreen(stakeDebit);
      if (nextScreen === "pending") {
        setIsSettingUp(true);
        for (let attempt = 0; attempt < APPLY_SETTLE_POLL_COUNT && nextScreen === "pending"; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, STATUS_POLL_INTERVAL_MS));
          if (!isMountedRef.current) return;
          await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
          nextScreen = outcomeScreen(queryClient.getQueryData(APIQueryKeys.stakeDebit)?.data);
        }
      }
      if (!isMountedRef.current) return;
      setIsSettingUp(false);
      setScreen(nextScreen);
    },
    [queryClient]
  );

  // While the application is processed (pending, under review, more details needed) keep checking
  // the debit status. The webhook opens the account and moves it to DepositAccountCreated.
  const isWaitingOnStatus = WAITING_SCREENS.includes(screen);
  const { data: polledStakeDebit } = useStakeDebit({
    enabled: isWaitingOnStatus,
    refetchInterval: isWaitingOnStatus ? STATUS_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (!isWaitingOnStatus || !polledStakeDebit?.data) return;
    const stakeDebit = polledStakeDebit.data;
    if (isSVDEligible(stakeDebit)) return;
    const nextScreen = outcomeScreen(stakeDebit);
    if (nextScreen !== screen) setScreen(nextScreen);
  }, [isWaitingOnStatus, polledStakeDebit, screen]);

  // Nothing left to do on pending or under review: send the member home after a while.
  const isPendingOrReview = screen === "pending" || screen === "review";
  useEffect(() => {
    if (!isPendingOrReview) return undefined;
    const timeout = setTimeout(onFinish, PENDING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isPendingOrReview, onFinish]);

  useEffect(() => {
    if (screen !== "opened") return undefined;
    const timeout = setTimeout(() => setScreen("card"), OPENED_ADVANCE_MS);
    return () => clearTimeout(timeout);
  }, [screen]);

  const { mutate: requestCard, isLoading: isRequestingCard } = useRequestStakeDebitCard({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
      if (isMountedRef.current) setScreen("cardDone");
    },
    onError: (error) => dispatchAPIErrorToast({ message: error?.response?.data?.error }),
  });

  const [paymentMethod, setPaymentMethod] = useState("stake");
  const [isSubmittingPower, setIsSubmittingPower] = useState(false);

  useEffect(() => {
    if (screen !== "powerLoading") return undefined;
    const timeout = setTimeout(() => setScreen("powerReview"), POWER_LOADING_MS);
    return () => clearTimeout(timeout);
  }, [screen]);

  // Placeholder until the Public Grid enrollment API is wired.
  const submitPower = () => {
    setIsSubmittingPower(true);
    setTimeout(() => {
      if (!isMountedRef.current) return;
      setIsSubmittingPower(false);
      setScreen("powerDone");
    }, POWER_SUBMIT_MS);
  };

  const { mutate: createOrder, isLoading: isRequestingCall } = useCreateMyUtilitiesOrder({
    onSuccess: () => {
      if (isMountedRef.current) setScreen("homeDone");
    },
    onError: (error) => dispatchAPIErrorToast({ message: error?.response?.data?.error }),
  });

  const offer = UTILITIES_OFFER;
  const totalLabel = amount ? formatMoney(amount + offer.powerCashBack + offer.homeServicesCashBack) : "";
  // $200 + $50 + up to $130 = $380 (Paper "Show me the money")
  const powerEarn = paymentMethod === "stake" ? offer.powerCashBack : offer.powerCardCashBack;

  let body = null;
  if (screen === "landing") {
    body = <Landing amount={amountLabel} onNext={() => setScreen("decide")} />;
  } else if (screen === "decide") {
    body = <Decide amount={amountLabel} offer={offer} onOpen={() => setScreen("goals")} onLater={onFinish} />;
  } else if (screen === "goals") {
    body = (
      <Goals
        selected={goals}
        onToggle={toggleGoal}
        onBack={() => setScreen("decide")}
        onContinue={() => continueFromGoals(false)}
        onSkip={() => continueFromGoals(true)}
      />
    );
  } else if (screen === "wizard") {
    body = (
      <div className="battle-cards-page bc-member-setup-page su-member-setup">
        <StakeCheckingWizard
          variant="page"
          hideMilestone
          onExit={() => setScreen("goals")}
          onComplete={onFinish}
          onVerificationRejected={showDenied}
          onDepositAccountCreated={showOpened}
          onApplied={showApplicationOutcome}
          onApplyingChange={setIsSettingUp}
        />
      </div>
    );
  } else if (screen === "pending" || screen === "review") {
    body = (
      <Card
        progress={90}
        tall
        footer={UTILITIES_STEPS_ENABLED ? <PrimaryButton onClick={() => setScreen("power")}>Finish Power setup</PrimaryButton> : null}
      >
        <div className="su-status">
          <CheckBadge />
          <h2 className="su-title">Your Stake Account application is in!</h2>
          <p className="su-subtitle">Most accounts open within an hour. We’ll text you when yours is ready.</p>
          {UTILITIES_STEPS_ENABLED ? null : (
            <Timeline
              items={[
                { when: "Today", title: "Application in", body: "Your part is done.", done: true },
                { when: "1 hr to 2 days", title: "Account opens", body: "We email and text you the moment it's ready." },
                { when: "Then", title: `${amountLabel || "Your cash back"} lands`, body: "Straight into your new Stake account." },
              ]}
            />
          )}
        </div>
      </Card>
    );
  } else if (screen === "details") {
    body = (
      <Card progress={90} tall footer={<PrimaryButton onClick={() => setScreen("wizard")}>Verify</PrimaryButton>}>
        <div className="su-status su-status--text">
          <h2 className="su-title">We need a few more details</h2>
          <p className="su-subtitle">Confirming your identity took an extra step this time. This will wrap it up.</p>
        </div>
      </Card>
    );
  } else if (screen === "opened") {
    body = (
      <Card progress={100}>
        <div className="su-status">
          <CheckBadge />
          <h2 className="su-title">Congrats! Your Stake account is open.</h2>
        </div>
      </Card>
    );
  } else if (screen === "card" && isRequestingCard) {
    body = (
      <Card progress={100} title="Set up Stake">
        <div className="su-loading">
          <CircularProgress size={36} sx={{ color: "#54FF99" }} />
        </div>
      </Card>
    );
  } else if (screen === "card") {
    body = (
      <Card
        progress={100}
        title="Set up Stake"
        footer={
          <>
            <PrimaryButton onClick={() => requestCard("VIRTUAL")}>Request card</PrimaryButton>
            <button className="su-link" type="button" onClick={() => setScreen("celebrate")}>
              Skip for now
            </button>
          </>
        }
      >
        <CardPair />
        <HeadlineBlock lines="Get Stake Debit." accent="Earn up to 4x Cash Back." sub="Use it anywhere debit cards are accepted." />
        <Tiles
          items={[
            { icon: TILE_ICONS.phone, label: "Virtual card in minutes" },
            { icon: TILE_ICONS.card, label: "Physical card mailed" },
            { icon: TILE_ICONS.clock, label: "4x Cash Back at select merchants" },
          ]}
        />
      </Card>
    );
  } else if (screen === "cardDone") {
    body = (
      <Card progress={100} title="Set up Stake" footer={<PrimaryButton onClick={() => setScreen("celebrate")}>Continue</PrimaryButton>}>
        <div className="su-status">
          <CheckBadge />
          <h2 className="su-title">Your virtual card is ready</h2>
          <p className="su-subtitle">Spend with it today. A physical card is on its way.</p>
        </div>
      </Card>
    );
  } else if (screen === "celebrate") {
    body = (
      <Celebration
        amount={amountLabel}
        total={UTILITIES_STEPS_ENABLED ? totalLabel : ""}
        powerAmount={offer.powerCashBack}
        homeAmount={offer.homeServicesCashBack}
        onContinue={onFinish}
        onMakeMore={UTILITIES_STEPS_ENABLED && totalLabel ? () => setScreen("power") : null}
      />
    );
  } else if (screen === "power") {
    body = (
      <PowerSignUp
        cashBack={offer.powerCashBack}
        onContinue={() => setScreen("powerLoading")}
        onLater={() => setScreen("homeServices")}
      />
    );
  } else if (screen === "powerLoading") {
    body = <PowerLoading />;
  } else if (screen === "powerReview") {
    body = (
      <PowerReview
        offer={offer}
        method={paymentMethod}
        onMethod={setPaymentMethod}
        onBack={() => setScreen("power")}
        onConfirm={submitPower}
        isSubmitting={isSubmittingPower}
      />
    );
  } else if (screen === "powerDone") {
    body = <PowerDone earn={powerEarn} homeAmount={offer.homeServicesCashBack} onContinue={() => setScreen("homeServices")} onFinish={onFinish} />;
  } else if (screen === "homeServices") {
    body = (
      <HomeServices
        cashBack={offer.homeServicesCashBack}
        onRequest={() => createOrder({ sfid: getUserSfid() })}
        onLater={onFinish}
        isLoading={isRequestingCall}
      />
    );
  } else if (screen === "homeDone") {
    body = <HomeDone cashBack={offer.homeInternetCashBack} onContinue={onFinish} />;
  } else if (screen === "denied") {
    // No denied screen is drawn yet (Paper H4 item 15). Placeholder without a check option.
    body = (
      <Card progress={80} tall footer={<PrimaryButton onClick={showIntercom}>Contact Support</PrimaryButton>}>
        <div className="su-status su-status--text">
          <h2 className="su-title">We couldn't approve your application</h2>
          <p className="su-subtitle">We're sorry, but we can't open a Stake account for you right now. If you have questions, contact Support.</p>
        </div>
      </Card>
    );
  }

  return (
    <Shell email={email || getUserEmail()} onSignOutClick={onSignOutClick}>
      {body}
      {isSettingUp ? <SettingUpOverlay /> : null}
    </Shell>
  );
}

export default function StakeUtilitiesPage() {
  const navigate = useNavigate();
  const { setAppUtilityProps } = useAppUtilityContext();

  useEffect(() => {
    setAppUtilityProps((props) => ({ ...props, hideNavigationBar: true }));
    return () => setAppUtilityProps((props) => ({ ...props, hideNavigationBar: false }));
  }, [setAppUtilityProps]);

  const { isLoading: isLoadingDeals, data: dealsData } = useDeals();
  const { isLoading: isLoadingStakeDebit, data: stakeDebitData } = useStakeDebit();
  const { isLoading: isLoadingUnclaimed, data: unclaimedTransactionsData } = useUnclaimedTransactions();
  const isLoading = isLoadingDeals || isLoadingStakeDebit || isLoadingUnclaimed;

  const isStakeUtilitiesUser = isStakeUtilitiesUserFromDealsResponse(dealsData);

  // Same gate as the other offers: an unclaimed rebate is required. Decide the entry screen once, because
  // applying changes the debit status and would otherwise fail these checks while the member is mid-flow.
  let liveScreen = null;
  if (shouldShowStakeUtilitiesPage(dealsData, unclaimedTransactionsData, stakeDebitData)) liveScreen = "landing";
  else if (shouldShowStakeUtilitiesResumePage(dealsData, unclaimedTransactionsData, stakeDebitData)) {
    liveScreen = outcomeScreen(stakeDebitData?.data);
  }

  const entryRef = useRef(null);
  if (!entryRef.current && !isLoading && isStakeUtilitiesUser && liveScreen) entryRef.current = liveScreen;
  const entryScreen = entryRef.current;

  const leave = useCallback(() => navigate("/home", { replace: true }), [navigate]);

  useEffect(() => {
    if (isLoading || entryScreen) return;
    navigate("/home", { replace: true });
  }, [isLoading, entryScreen, navigate]);

  const amount = getBattlecardCashBackAmountNumeric(dealsData, unclaimedTransactionsData);

  if (isLoading || !entryScreen) {
    return (
      <div className="stake-utilities stake-utilities-loading">
        <CircularProgress sx={{ color: "#54FF99" }} />
      </div>
    );
  }

  return <StakeUtilities entryScreen={entryScreen} amount={amount} onFinish={leave} onSignOutClick={onSignOut} />;
}
